import { useState } from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, Link as MuiLink, MenuItem, Snackbar, Stack, Switch, TextField, Tooltip, Typography,
} from "@mui/material";
import { useNavigate } from "react-router";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import LockResetIcon from "@mui/icons-material/LockReset";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";

/** Gera senha forte (sem caracteres ambíguos) usando CSPRNG. */
function genPassword(len = 16): string {
  const sets = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*?";
  const arr = new Uint32Array(len);
  crypto.getRandomValues(arr);
  return Array.from(arr, (n) => sets[n % sets.length]).join("");
}
/** Copia para a área de transferência sem exibir o valor na tela. */
async function copyToClipboard(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
}
import { AccessDrawer } from "./AccessDrawer";
import { ImpersonateDialog } from "./ImpersonateDialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { GridColDef } from "@mui/x-data-grid";
import { ConfirmDialog, ServerDataGrid, formatRelativeFromNow, formatCpf, useDataGridState } from "@safrater/ui";
import { api } from "../../api/client";
import { useAuth } from "../../api/auth-context";

interface UserRow {
  id: string;
  name: string;
  email: string;
  cpf: string;
  status: "active" | "pending_approval" | "pending_setup" | "suspended";
  isMasterAdmin: boolean;
  lastLoginAt: string | null;
  institutions: { id: string; name: string }[];
}

interface InstitutionLite { id: string; name: string }

const STATUS_COLORS: Record<string, "success" | "warning" | "error" | "default"> = {
  active: "success",
  pending_approval: "warning",
  pending_setup: "warning",
  suspended: "error",
};

interface UserDialogState {
  open: boolean;
  mode: "create" | "edit";
  row?: UserRow;
}

export function UsersPage() {
  const state = useDataGridState();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMaster = !!user?.isMasterAdmin;
  const [dlg, setDlg] = useState<UserDialogState>({ open: false, mode: "create" });
  const [confirmDelete, setConfirmDelete] = useState<UserRow | null>(null);
  const [accessUser, setAccessUser] = useState<UserRow | null>(null);
  const [impersonateTarget, setImpersonateTarget] = useState<UserRow | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ["users", state.queryParams],
    queryFn: async () => (await api.get<{ data: UserRow[]; total: number }>("/users", { params: state.queryParams })).data,
    placeholderData: (prev) => prev,
  });

  const institutionsQ = useQuery({
    queryKey: ["institutions-lite"],
    queryFn: async () => (await api.get<{ data: InstitutionLite[] }>("/institutions", { params: { page_size: 200 } })).data.data,
    enabled: dlg.open,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["users"] });

  const createM = useMutation({
    mutationFn: (body: Partial<UserRow> & { cpf: string; institutionId?: string }) => api.post("/users", body),
    onSuccess: () => { invalidate(); setDlg({ open: false, mode: "create" }); },
  });
  const updateM = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<UserRow> }) => api.patch(`/users/${id}`, body),
    onSuccess: () => { invalidate(); setDlg({ open: false, mode: "create" }); },
  });
  const setStatusM = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => api.patch(`/users/${id}/status`, { status }),
    onSuccess: invalidate,
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => api.delete(`/users/${id}`),
    onSuccess: () => { invalidate(); setConfirmDelete(null); },
  });
  // Redefine senha: gera senha forte, copia pro clipboard (sem exibir) e envia.
  const resetPwdM = useMutation({
    mutationFn: async (row: UserRow) => {
      const pwd = genPassword();
      const copied = await copyToClipboard(pwd);
      await api.post(`/users/${row.id}/password`, { password: pwd });
      return { copied, name: row.name };
    },
    onSuccess: ({ copied, name }) => {
      invalidate();
      setNotice(copied ? `Nova senha de ${name} gerada e copiada para a área de transferência.` : `Senha redefinida (falha ao copiar — gere novamente).`);
    },
    onError: () => setNotice("Não foi possível redefinir a senha."),
  });

  const columns: GridColDef<UserRow>[] = [
    {
      field: "name", headerName: "Nome", flex: 1, minWidth: 200,
      renderCell: ({ row }) => (
        <MuiLink component="button" type="button" underline="hover" onClick={() => navigate(`/users/${row.id}`)} sx={{ textAlign: "left" }}>
          {row.name}
        </MuiLink>
      ),
    },
    { field: "email", headerName: "Email", flex: 1, minWidth: 220 },
    { field: "cpf", headerName: "CPF", width: 160, valueFormatter: (v) => formatCpf(v as string) },
    {
      field: "status", headerName: "Status", width: 160,
      renderCell: ({ value }) => <Chip size="small" label={value as string} color={STATUS_COLORS[value as string] ?? "default"} />,
    },
    {
      field: "isMasterAdmin", headerName: "Master", width: 100, type: "boolean",
      renderCell: ({ value }) => (value ? <Chip size="small" color="primary" label="Master" /> : null),
    },
    {
      field: "institutions", headerName: "Instituições", flex: 1, minWidth: 220, sortable: false, filterable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {row.institutions.slice(0, 2).map((i) => <Chip key={i.id} label={i.name} size="small" />)}
          {row.institutions.length > 2 && <Chip label={`+${row.institutions.length - 2}`} size="small" />}
        </Stack>
      ),
    },
    {
      field: "lastLoginAt", headerName: "Último acesso", width: 160, type: "date",
      valueGetter: (v) => (v ? new Date(v as string) : null),
      renderCell: ({ value }) => (value ? formatRelativeFromNow((value as Date).toISOString()) : "—"),
    },
    ...(isMaster ? [{
      field: "_actions", headerName: "Ações", width: 180, sortable: false, filterable: false,
      renderCell: ({ row }: { row: UserRow }) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Editar">
            <IconButton size="small" aria-label="Editar" onClick={() => setDlg({ open: true, mode: "edit", row })}><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Acessos (roles + grupos)">
            <IconButton size="small" aria-label="Acessos" onClick={() => setAccessUser(row)}><VpnKeyIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Redefinir senha (gera e copia, sem exibir)">
            <IconButton size="small" aria-label="Redefinir senha" onClick={() => resetPwdM.mutate(row)} disabled={resetPwdM.isPending}><LockResetIcon fontSize="small" /></IconButton>
          </Tooltip>
          {row.id !== user?.sub && row.status === "active" && (
            <Tooltip title="Impersonate (assumir sessao deste usuario)">
              <IconButton size="small" aria-label="Impersonate" color="warning" onClick={() => setImpersonateTarget(row)}>
                <SwitchAccountIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {row.status === "active" ? (
            <Tooltip title="Suspender">
              <IconButton size="small" aria-label="Suspender" onClick={() => setStatusM.mutate({ id: row.id, status: "suspended" })}><BlockIcon fontSize="small" /></IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Reativar">
              <IconButton size="small" aria-label="Reativar" onClick={() => setStatusM.mutate({ id: row.id, status: "active" })}><CheckIcon fontSize="small" /></IconButton>
            </Tooltip>
          )}
          <Tooltip title="Excluir">
            <IconButton size="small" aria-label="Excluir" color="error" onClick={() => setConfirmDelete(row)}><DeleteIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Stack>
      ),
    } as GridColDef<UserRow>] : []),
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h4">Usuários</Typography>
        {isMaster && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDlg({ open: true, mode: "create" })}>
            Novo usuário
          </Button>
        )}
      </Stack>
      <Box sx={{ height: 640 }}>
        <ServerDataGrid<UserRow>
          gridId="central.users"
          userScope={user?.sub}
          rows={data?.data ?? []}
          rowCount={data?.total ?? 0}
          columns={columns}
          loading={isFetching}
          paginationModel={state.pagination}
          onPaginationModelChange={state.setPagination}
          sortModel={state.sort}
          onSortModelChange={state.setSort}
          filterModel={state.filter}
          onFilterModelChange={state.setFilter}
        />
      </Box>

      {dlg.open && (
        <UserFormDialog
          key={`${dlg.mode}-${dlg.row?.id ?? "new"}`}
          state={dlg}
          institutions={institutionsQ.data ?? []}
          onClose={() => setDlg({ open: false, mode: "create" })}
          onSubmit={(values) => {
            if (dlg.mode === "edit" && dlg.row) {
              updateM.mutate({ id: dlg.row.id, body: { name: values.name, email: values.email, isMasterAdmin: values.isMasterAdmin } });
            } else {
              createM.mutate(values);
            }
          }}
          submitting={createM.isPending || updateM.isPending}
          error={(createM.error || updateM.error) ? "Não foi possível salvar." : null}
        />
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Excluir usuário"
        description={`Confirmar exclusão de "${confirmDelete?.name}"? O usuário será suspenso e perderá acesso.`}
        confirmLabel="Excluir"
        severity="error"
        loading={deleteM.isPending}
        onConfirm={() => { if (confirmDelete) deleteM.mutate(confirmDelete.id); }}
        onCancel={() => setConfirmDelete(null)}
      />

      <AccessDrawer
        open={!!accessUser}
        userId={accessUser?.id ?? null}
        userName={accessUser?.name}
        onClose={() => setAccessUser(null)}
      />

      {/* Impersonate disparado pela coluna Acoes — pre-seleciona o user clicado.
          O dialog faz POST /api/auth/impersonate/:id e hard-reload em "/" no sucesso. */}
      <ImpersonateDialog
        open={!!impersonateTarget}
        currentUserId={user?.sub ?? null}
        onClose={() => setImpersonateTarget(null)}
        prefilled={impersonateTarget ? {
          id: impersonateTarget.id,
          name: impersonateTarget.name,
          email: impersonateTarget.email,
          cpf: impersonateTarget.cpf,
          isMasterAdmin: impersonateTarget.isMasterAdmin,
          status: impersonateTarget.status,
          institutions: impersonateTarget.institutions,
        } : null}
      />

      <Snackbar
        open={!!notice}
        autoHideDuration={6000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setNotice(null)} variant="filled">{notice}</Alert>
      </Snackbar>
    </Box>
  );
}

interface UserFormValues {
  name: string;
  email: string;
  cpf: string;
  isMasterAdmin: boolean;
  institutionId?: string;
  password?: string;
}

function UserFormDialog({
  state, institutions, onClose, onSubmit, submitting, error,
}: {
  state: UserDialogState;
  institutions: InstitutionLite[];
  onClose: () => void;
  onSubmit: (v: UserFormValues) => void;
  submitting: boolean;
  error: string | null;
}) {
  const [values, setValues] = useState<UserFormValues>(() => ({
    name: state.row?.name ?? "",
    email: state.row?.email ?? "",
    cpf: state.row?.cpf ?? "",
    isMasterAdmin: state.row?.isMasterAdmin ?? false,
    institutionId: "",
  }));
  const [pwdState, setPwdState] = useState<"" | "copied" | "failed">("");

  const generatePassword = async () => {
    const p = genPassword();
    const copied = await copyToClipboard(p);
    setValues((v) => ({ ...v, password: p }));
    setPwdState(copied ? "copied" : "failed");
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { component: "form", onSubmit: (e: React.FormEvent) => { e.preventDefault(); onSubmit(values); } } }}
    >
      <DialogTitle>{state.mode === "edit" ? "Editar usuário" : "Novo usuário"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Typography color="error" variant="body2">{error}</Typography>}
          <TextField label="Nome" required value={values.name} onChange={(e) => setValues({ ...values, name: e.target.value })} fullWidth />
          <TextField label="Email" type="email" required value={values.email} onChange={(e) => setValues({ ...values, email: e.target.value })} fullWidth />
          <TextField
            label="CPF"
            required
            value={values.cpf}
            onChange={(e) => setValues({ ...values, cpf: e.target.value.replace(/\D/g, "").slice(0, 11) })}
            disabled={state.mode === "edit"}
            helperText={state.mode === "edit" ? "CPF não pode ser alterado" : "Somente números"}
            fullWidth
          />
          {state.mode === "create" && (
            <TextField
              select label="Instituição (opcional)" value={values.institutionId ?? ""}
              onChange={(e) => setValues({ ...values, institutionId: e.target.value || undefined })} fullWidth
            >
              <MenuItem value="">Sem instituição</MenuItem>
              {institutions.map((i) => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
            </TextField>
          )}
          {state.mode === "create" && (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="outlined" startIcon={<VpnKeyIcon />} onClick={generatePassword}>Gerar senha</Button>
                {pwdState === "copied" && <Chip size="small" color="success" label="Senha gerada e copiada ✓" />}
                {pwdState === "failed" && <Chip size="small" color="warning" label="Gerada (copie manualmente não disponível)" />}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                A senha não é exibida — foi copiada para a área de transferência. Sem senha, o usuário recebe link de definição por email.
              </Typography>
            </Box>
          )}
          <FormControlLabel
            control={<Switch checked={values.isMasterAdmin} onChange={(_, v) => setValues({ ...values, isMasterAdmin: v })} />}
            label="Master admin (acesso total ao sistema)"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button type="submit" variant="contained" disabled={submitting}>Salvar</Button>
      </DialogActions>
    </Dialog>
  );
}
